// Vendor partner page configurations for all categories
// Each config drives the VendorPartnerPage template

const VENDOR_CONFIGS = {
  dj: {
    slug: 'dj',
    title: 'DJs',
    singular: 'DJ',
    headline: 'Denver Needs\nYour Sound.',
    subheadline: 'Join Eventism and get booked at venues you never had access to. We bring the crowd — you bring the beats.',
    heroImage: 'https://images.unsplash.com/photo-1583906703597-f0a9a127c18a?w=800&q=80',
    heroImage2: 'https://images.unsplash.com/photo-1540039155733-5bb30b53aa14?w=800&q=80',
    metaTitle: 'Partner With Eventism as a DJ | Get More Bookings in Denver',
    metaDescription: 'Join Denver\'s top event platform as a DJ partner. Get booked at premium venues, zero marketing spend, and keep 100% of your tips. Apply now.',
    benefitCards: [
      { icon: 'MusicNotes', title: 'Premium Venue Access', desc: 'Get booked at Denver\'s top event centers, hotels, and rooftops that you couldn\'t access on your own.' },
      { icon: 'ChartLineUp', title: 'Consistent Bookings', desc: 'Our marketing engine targets event planners actively looking for DJs. No more chasing leads.' },
      { icon: 'CurrencyDollar', title: 'Keep 100% of Tips', desc: 'You set your rates. You keep all tips. We charge a small platform fee — that\'s it.' },
      { icon: 'Megaphone', title: 'Zero Marketing Spend', desc: 'We build your marketing pages, run the ads, and handle the SEO. You just show up and perform.' },
      { icon: 'Star', title: 'Build Your Brand', desc: 'Get a professional vendor profile, reviews from real clients, and a portfolio showcase.' },
    ],
    strategySteps: [
      { num: '01', title: 'Venues Partner With Us', desc: 'We onboard Denver\'s top event venues — hotels, rooftops, ballrooms, corporate centers. They trust us to bring them the best talent.' },
      { num: '02', title: 'Clients Book Through Eventism', desc: 'Corporate planners, wedding couples, and party organizers use our platform to plan their events. They need a DJ.' },
      { num: '03', title: 'We Recommend You Exclusively', desc: 'When a client books a venue, our system matches them with the best DJ for the job — that\'s you. Direct bookings, no bidding wars.' },
      { num: '04', title: 'You Get Paid, We Grow Together', desc: 'Fast payouts, transparent pricing, and a growing network. More venues = more events = more gigs for you.' },
    ],
    stats: [
      { value: '140%', label: 'Average booking increase for DJs in their first 90 days' },
      { value: '$2,400', label: 'Average monthly earnings for active DJ partners' },
      { value: '50+', label: 'Venue partners across the Denver metro area' },
    ],
    galleryImages: [
      'https://images.unsplash.com/photo-1672941914006-c34257b1ee31?w=600&q=80',
      'https://images.unsplash.com/photo-1541020230467-e33e4f96f7fc?w=600&q=80',
      'https://images.unsplash.com/photo-1639682687092-0264e96cbb71?w=600&q=80',
      'https://images.unsplash.com/photo-1627610941381-ea4f3ff83e58?w=600&q=80',
    ],
    faqs: [
      { q: 'How much does it cost to join?', a: 'It\'s free to create a vendor profile. We charge a small platform fee only when you get booked through Eventism.' },
      { q: 'Do I need to be available full-time?', a: 'Not at all. You set your own availability. Whether you DJ weekends-only or every night, you\'re in control.' },
      { q: 'What kind of events will I be booked for?', a: 'Corporate events, weddings, private parties, holiday galas, club nights, and more. Denver has a thriving event scene.' },
      { q: 'Can I still take bookings outside the platform?', a: 'Absolutely. Eventism is an additional channel for you — not a replacement for your existing business.' },
      { q: 'How do I get paid?', a: 'Fast payouts directly to your bank account. We handle all payment processing and invoicing.' },
    ],
    ctaText: 'Start Getting Booked',
    ctaSubtext: 'Join 50+ venue partners and hundreds of event clients already on the platform.',
    sampleListing: {
      name: 'DJ Marcus Cole',
      tagline: 'Keeping Denver dancing since 2018',
      rating: 4.9,
      reviews: 47,
      priceLabel: 'From $500/event',
      tags: ['Weddings', 'Corporate', 'Club Nights', 'Bilingual'],
      features: ['Professional Sound System', 'LED Lighting Package', 'MC Services Available', 'Custom Playlists'],
    },
    sampleMarketingPage: {
      headline: 'Make Your Event Unforgettable with Denver\'s Top DJ',
      subline: 'DJ Marcus Cole brings the perfect energy to weddings, corporate events, and celebrations across the Denver metro.',
      testimonial: '"Marcus read the room perfectly. Our wedding dance floor was packed all night. Best decision we made!" — Sarah & James, October 2025',
      packages: [
        { name: 'Essential', price: '$500', desc: '4 hours, basic sound' },
        { name: 'Premium', price: '$900', desc: '6 hours, lighting + MC' },
        { name: 'Ultimate', price: '$1,500', desc: 'Full night, premium everything' },
      ],
    },
    platformPreview: {
      cardTitle: 'Professional DJ Entertainment',
      cardDescription: 'High-energy DJ sets with pro sound and lighting',
      cardCategory: 'Entertainment',
      cardSubcategory: 'Party',
      cardPrice: '$200.00',
      cardPriceUnit: '/hour',
      cardPriceLabel: 'Per Hour',
      cardBadges: ['Featured', 'On-Site'],
      cardImage: 'https://images.unsplash.com/photo-1583906703597-f0a9a127c18a?w=600&q=80',
      modalImages: [
        'https://images.unsplash.com/photo-1583906703597-f0a9a127c18a?w=800&q=80',
        'https://images.unsplash.com/photo-1540039155733-5bb30b53aa14?w=400&q=80',
        'https://images.unsplash.com/photo-1672941914006-c34257b1ee31?w=400&q=80',
        'https://images.unsplash.com/photo-1639682687092-0264e96cbb71?w=400&q=80',
        'https://images.unsplash.com/photo-1541020230467-e33e4f96f7fc?w=400&q=80',
      ],
      modalWhatToExpect: 'Our DJ arrives 1 hour before your event for full sound check and setup. Expect seamless mixing across genres, professional MC services if requested, and dynamic LED lighting that transforms your venue. We coordinate with your event planner for announcements, first dances, and special moments.',
      modalServiceType: 'Full Package',
      modalIncludes: ['Professional Sound System', 'LED Lighting Package', 'MC Services', 'Custom Playlist Curation', 'Setup & Teardown'],
    },
  },

  catering: {
    slug: 'catering',
    title: 'Caterers',
    singular: 'Caterer',
    headline: 'Feed Denver\'s\nBiggest Events.',
    subheadline: 'Partner with Eventism to access premium corporate and social events. We connect you directly with clients who need your food.',
    heroImage: 'https://images.unsplash.com/photo-1555244162-803834f70033?w=800&q=80',
    heroImage2: 'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=800&q=80',
    metaTitle: 'Partner With Eventism as a Caterer | Grow Your Catering Business in Denver',
    metaDescription: 'Join Denver\'s top event platform as a catering partner. Access premium venues, corporate clients, and steady bookings. Apply now.',
    benefitCards: [
      { icon: 'ForkKnife', title: 'Premium Client Access', desc: 'Get connected with corporate planners, wedding coordinators, and private event hosts with real budgets.' },
      { icon: 'ChartLineUp', title: 'Steady Event Pipeline', desc: 'Our outreach targets companies and planners actively searching for catering. No more cold calling.' },
      { icon: 'CurrencyDollar', title: 'You Set Your Prices', desc: 'Full control over your menu pricing. Per person, flat rate, or custom packages — your call.' },
      { icon: 'Megaphone', title: 'Marketing Done For You', desc: 'Professional listings, custom marketing pages, and SEO that puts your business in front of the right people.' },
      { icon: 'UsersThree', title: 'Scale Your Team', desc: 'Need extra hands? Our staffing network provides trained servers, bartenders, and prep cooks on demand.' },
    ],
    strategySteps: [
      { num: '01', title: 'Venues Trust Eventism', desc: 'Denver\'s best event centers, hotels, and corporate spaces partner with us. When they have events, they need catering.' },
      { num: '02', title: 'Clients Plan Through Our Platform', desc: 'Event planners use Eventism to find and book everything — including food. Your catering shows up as a top recommendation.' },
      { num: '03', title: 'Direct Bookings, No Bidding', desc: 'Clients choose you based on your menu, reviews, and portfolio. No race to the bottom on price.' },
      { num: '04', title: 'Grow With Every Venue We Add', desc: 'Every new venue partner means more events. More events means more catering gigs flowing to you.' },
    ],
    stats: [
      { value: '200%', label: 'Average revenue increase for caterers in year one' },
      { value: '$8,500', label: 'Average monthly earnings for active catering partners' },
      { value: '50+', label: 'Venue partners sending you business' },
    ],
    galleryImages: [
      'https://images.unsplash.com/photo-1555244162-803834f70033?w=600&q=80',
      'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=600&q=80',
      'https://images.unsplash.com/photo-1530103862676-de8c9debad1d?w=600&q=80',
      'https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=600&q=80',
    ],
    faqs: [
      { q: 'How much does it cost to join?', a: 'Free to join. We charge a small platform fee only when you receive a booking through Eventism.' },
      { q: 'What types of events will I cater?', a: 'Corporate lunches, galas, weddings, holiday parties, team-building events, and more.' },
      { q: 'Can I showcase my specific cuisine style?', a: 'Yes! Your profile features your menus, dietary options, cuisine type, and pricing models.' },
      { q: 'Do you handle dietary restrictions?', a: 'Clients specify their needs during booking. You\'ll see all requirements before accepting.' },
      { q: 'How quickly do I get paid?', a: 'Fast payouts after event completion. All payment processing is handled by the platform.' },
    ],
    ctaText: 'Start Catering More Events',
    ctaSubtext: 'Denver\'s events need your food. Let us connect you.',
    sampleListing: {
      name: 'Rocky Mountain Catering Co.',
      tagline: 'Farm-to-table catering for unforgettable events',
      rating: 4.8,
      reviews: 62,
      priceLabel: 'From $45/person',
      tags: ['Farm-to-Table', 'Corporate', 'Weddings', 'Vegan Options'],
      features: ['Full-Service Catering', 'Custom Menu Design', 'Dietary Accommodations', 'On-Site Chef'],
    },
    sampleMarketingPage: {
      headline: 'Elevate Your Event With Award-Winning Catering',
      subline: 'Rocky Mountain Catering Co. brings locally-sourced, chef-crafted menus to Denver\'s most memorable events.',
      testimonial: '"The food was the highlight of our gala. Every single guest commented on it. Absolutely phenomenal service." — Denver Chamber of Commerce, 2025',
      packages: [
        { name: 'Appetizer Bar', price: '$35/pp', desc: 'Passed hors d\'oeuvres' },
        { name: 'Plated Dinner', price: '$65/pp', desc: '3-course plated meal' },
        { name: 'Full Service', price: '$95/pp', desc: 'Premium buffet + bar' },
      ],
    },
    platformPreview: {
      cardTitle: 'Build-Your-Own Taco Bar',
      cardDescription: 'Premium taco bar with customizable options',
      cardCategory: 'Food & Catering',
      cardSubcategory: 'Dinner',
      cardPrice: '$18.00',
      cardPriceUnit: '/person',
      cardPriceLabel: 'Per Person',
      cardBadges: ['Featured', 'Drop-off'],
      cardImage: 'https://images.unsplash.com/photo-1565299585323-38d6b0865b47?w=600&q=80',
      modalImages: [
        'https://images.unsplash.com/photo-1565299585323-38d6b0865b47?w=800&q=80',
        'https://images.unsplash.com/photo-1551504734-5ee1c4a1479b?w=400&q=80',
        'https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=400&q=80',
        'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=400&q=80',
        'https://images.unsplash.com/photo-1555244162-803834f70033?w=400&q=80',
      ],
      modalWhatToExpect: 'Our culinary team arrives 2 hours before service to set up a beautiful, Instagram-worthy taco station. Guests will enjoy watching our chefs prepare fresh tortillas and assemble tacos with their choice of premium proteins and house-made salsas. We handle everything from setup to cleanup.',
      modalServiceType: 'Full Service',
      modalIncludes: ['Premium Proteins (Carne Asada, Pollo, Carnitas)', 'Fresh Handmade Tortillas', 'House-Made Salsas & Guacamole', 'Full Setup & Cleanup', 'Serving Staff Included'],
    },
  },

  chef: {
    slug: 'chef',
    title: 'Chefs',
    singular: 'Chef',
    headline: 'Your Kitchen.\nDenver\'s Stage.',
    subheadline: 'From private dinners to corporate galas — bring your culinary talent to Denver\'s most exciting events through Eventism.',
    heroImage: 'https://images.unsplash.com/photo-1556910103-1c02745aae4d?w=800&q=80',
    heroImage2: 'https://images.unsplash.com/photo-1577219491135-ce391730fb2c?w=800&q=80',
    metaTitle: 'Partner With Eventism as a Chef | Private Chef Bookings in Denver',
    metaDescription: 'Join Denver\'s top event platform as a chef partner. Get booked for private dinners, corporate events, and exclusive experiences.',
    benefitCards: [
      { icon: 'CookingPot', title: 'Private & Corporate Events', desc: 'Get booked for intimate dinners, corporate team meals, and VIP experiences at Denver\'s best venues.' },
      { icon: 'ChartLineUp', title: 'Consistent Demand', desc: 'Our platform connects you with clients who want a personal chef experience — not just catering.' },
      { icon: 'CurrencyDollar', title: 'Premium Pricing', desc: 'Private chef experiences command premium rates. Set your pricing and showcase your signature dishes.' },
      { icon: 'Megaphone', title: 'Build Your Reputation', desc: 'Professional profile, client reviews, and custom marketing pages that showcase your culinary art.' },
      { icon: 'Star', title: 'Creative Freedom', desc: 'Design custom menus, offer tasting experiences, and build your brand as Denver\'s go-to event chef.' },
    ],
    strategySteps: [
      { num: '01', title: 'Venues & Clients Trust Us', desc: 'Denver\'s venues and event planners use Eventism to find premium culinary talent.' },
      { num: '02', title: 'Clients Want Personal Touch', desc: 'More people want private chef experiences. Our platform matches them with chefs like you.' },
      { num: '03', title: 'We Handle the Business Side', desc: 'Invoicing, contracts, marketing — we take care of it so you can focus on cooking.' },
      { num: '04', title: 'Your Network Grows With Us', desc: 'Every new venue and client on the platform is a potential booking for you.' },
    ],
    stats: [
      { value: '$3,500', label: 'Average private chef event booking' },
      { value: '180%', label: 'Growth in private chef demand since 2024' },
      { value: '50+', label: 'Venue partners across Denver' },
    ],
    galleryImages: [
      'https://images.unsplash.com/photo-1556910103-1c02745aae4d?w=600&q=80',
      'https://images.unsplash.com/photo-1577219491135-ce391730fb2c?w=600&q=80',
      'https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=600&q=80',
      'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=600&q=80',
    ],
    faqs: [
      { q: 'Is this for private chefs or catering chefs?', a: 'Both! Whether you do intimate 10-person dinners or 200-person galas, there\'s a place for you.' },
      { q: 'Do I need my own kitchen or equipment?', a: 'Most bookings are at venues with full kitchen facilities. You bring the talent, the venue provides the space.' },
      { q: 'How do I showcase my menus?', a: 'Your profile features photos, menu samples, cuisine styles, and pricing. Clients browse before booking.' },
      { q: 'Can I work with my own team?', a: 'Absolutely. Bring your sous chef, or use our staffing network for additional support.' },
      { q: 'What\'s the commission structure?', a: 'A small platform fee per booking. You keep the vast majority of every event.' },
    ],
    ctaText: 'Start Cooking at Denver\'s Best Events',
    ctaSubtext: 'Your talent deserves a bigger stage.',
    sampleListing: {
      name: 'Chef Isabella Reyes',
      tagline: 'Private chef experiences that tell a story',
      rating: 5.0, reviews: 28,
      priceLabel: 'From $150/person',
      tags: ['Private Dining', 'Latin Fusion', 'Farm-to-Table', 'Tasting Menus'],
      features: ['Custom Menu Design', 'Wine Pairing', 'Interactive Cooking', 'Dietary Specialists'],
    },
    sampleMarketingPage: {
      headline: 'An Intimate Culinary Experience for Your Next Event',
      subline: 'Chef Isabella creates personalized tasting menus that turn any event into a dining experience your guests will talk about for years.',
      testimonial: '"Chef Isabella turned our anniversary dinner into a Michelin-star experience. The flavors, the presentation, the storytelling — magical." — The Hendersons',
      packages: [
        { name: 'Intimate', price: '$150/pp', desc: '5-course tasting, up to 12 guests' },
        { name: 'Signature', price: '$200/pp', desc: '7-course + wine pairing' },
        { name: 'Chef\'s Table', price: '$300/pp', desc: 'Full interactive experience' },
      ],
    },
    platformPreview: {
      cardTitle: 'Private Chef Tasting Experience',
      cardDescription: 'Personalized multi-course tasting menus for intimate events',
      cardCategory: 'Private Dining',
      cardSubcategory: 'Tasting Menu',
      cardPrice: '$150.00',
      cardPriceUnit: '/person',
      cardPriceLabel: 'Per Person',
      cardBadges: ['Featured', 'On-Site'],
      cardImage: 'https://images.unsplash.com/photo-1556910103-1c02745aae4d?w=600&q=80',
      modalImages: [
        'https://images.unsplash.com/photo-1556910103-1c02745aae4d?w=800&q=80',
        'https://images.unsplash.com/photo-1577219491135-ce391730fb2c?w=400&q=80',
        'https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=400&q=80',
        'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=400&q=80',
        'https://images.unsplash.com/photo-1555244162-803834f70033?w=400&q=80',
      ],
      modalWhatToExpect: 'Chef Isabella arrives 3 hours before service with all ingredients, prepared and ready to create magic. She\'ll set up an intimate cooking station where guests can watch as each course comes to life. Expect 5-7 beautifully plated courses with wine pairing suggestions and personal stories behind each dish.',
      modalServiceType: 'Full Experience',
      modalIncludes: ['5-7 Course Tasting Menu', 'Wine Pairing Recommendations', 'Interactive Cooking Demonstration', 'All Ingredients & Equipment', 'Full Cleanup Included'],
    },
  },

  photography: {
    slug: 'photography',
    title: 'Photographers',
    singular: 'Photographer',
    headline: 'Capture Denver\'s\nBest Moments.',
    subheadline: 'Join Eventism and shoot at venues you\'ve always dreamed of. We connect you with clients who value your art.',
    heroImage: 'https://images.unsplash.com/photo-1554048612-b6a482bc67e5?w=800&q=80',
    heroImage2: 'https://images.unsplash.com/photo-1519741497674-611481863552?w=800&q=80',
    metaTitle: 'Partner With Eventism as a Photographer | Event Photography in Denver',
    metaDescription: 'Join Denver\'s top event platform as a photography partner. Shoot at premium venues and get steady bookings.',
    benefitCards: [
      { icon: 'Camera', title: 'Premium Venue Shoots', desc: 'Photograph events at Denver\'s most beautiful venues — from mountain-view rooftops to elegant ballrooms.' },
      { icon: 'ChartLineUp', title: 'Steady Booking Flow', desc: 'Our platform connects you with weddings, corporate events, and galas that need professional photography.' },
      { icon: 'CurrencyDollar', title: 'Your Rates, Your Rules', desc: 'Set hourly or package pricing. Offer add-ons like prints, albums, or drone footage.' },
      { icon: 'Megaphone', title: 'Portfolio Showcase', desc: 'Your vendor profile becomes a living portfolio. Clients see your best work before booking.' },
      { icon: 'UsersThree', title: 'Second Shooter Network', desc: 'Need a second shooter? Our vendor network connects you with fellow photographers.' },
    ],
    strategySteps: [
      { num: '01', title: 'Venues Choose Eventism', desc: 'Denver\'s top venues partner with us to provide their clients with trusted vendors.' },
      { num: '02', title: 'Every Event Needs Photos', desc: 'Whether it\'s a wedding, corporate gala, or product launch — photography is always needed.' },
      { num: '03', title: 'Clients Find You Through Us', desc: 'We match event types to photographer styles. Your specialty becomes your advantage.' },
      { num: '04', title: 'Reviews Build Your Business', desc: 'Happy clients leave reviews that drive more bookings. It\'s a flywheel.' },
    ],
    stats: [
      { value: '$1,800', label: 'Average event photography booking' },
      { value: '35+', label: 'Events per month needing photographers' },
      { value: '50+', label: 'Venue partners in the network' },
    ],
    galleryImages: [
      'https://images.unsplash.com/photo-1554048612-b6a482bc67e5?w=600&q=80',
      'https://images.unsplash.com/photo-1519741497674-611481863552?w=600&q=80',
      'https://images.unsplash.com/photo-1511285560929-80b456fea0bc?w=600&q=80',
      'https://images.unsplash.com/photo-1464366400600-7168b8af9bc3?w=600&q=80',
    ],
    faqs: [
      { q: 'What types of events will I photograph?', a: 'Weddings, corporate events, parties, product launches, headshots — the full spectrum.' },
      { q: 'Do I need specific equipment?', a: 'Professional-grade camera gear is expected. Specific requirements vary by event type.' },
      { q: 'Can I offer video as well?', a: 'Yes! Many clients want photo + video packages. You can list both services.' },
      { q: 'How do clients choose their photographer?', a: 'Through your portfolio, reviews, pricing, and style tags on your vendor profile.' },
      { q: 'What\'s the turnaround expectation?', a: 'You set your own delivery timelines. Clients see this before booking.' },
    ],
    ctaText: 'Start Shooting at Premium Events',
    ctaSubtext: 'Denver\'s events are waiting for your lens.',
    sampleListing: {
      name: 'Aspen Light Photography',
      tagline: 'Timeless moments, modern storytelling',
      rating: 4.9, reviews: 83,
      priceLabel: 'From $1,200/event',
      tags: ['Weddings', 'Editorial', 'Corporate', 'Drone'],
      features: ['Same-Day Previews', 'Online Gallery', 'Print Packages', 'Second Shooter'],
    },
    sampleMarketingPage: {
      headline: 'Capture Every Moment of Your Denver Event',
      subline: 'Aspen Light Photography brings a documentary approach to event coverage — authentic, beautiful, and unforgettable.',
      testimonial: '"The photos from our wedding still make me cry. Every candid, every detail — they captured it all perfectly." — Amanda & Tyler, June 2025',
      packages: [
        { name: 'Coverage', price: '$1,200', desc: '4 hours, 200+ edited' },
        { name: 'Premium', price: '$2,200', desc: '8 hours + engagement' },
        { name: 'Luxury', price: '$3,500', desc: 'Full day + album' },
      ],
    },
    platformPreview: {
      cardTitle: 'Full-Day Event Photography',
      cardDescription: 'Documentary-style event coverage with same-day previews',
      cardCategory: 'Photography',
      cardSubcategory: 'Coverage',
      cardPrice: '$1,200.00',
      cardPriceUnit: '/event',
      cardPriceLabel: 'Per Event',
      cardBadges: ['Featured', 'On-Site'],
      cardImage: 'https://images.unsplash.com/photo-1554048612-b6a482bc67e5?w=600&q=80',
      modalImages: [
        'https://images.unsplash.com/photo-1554048612-b6a482bc67e5?w=800&q=80',
        'https://images.unsplash.com/photo-1519741497674-611481863552?w=400&q=80',
        'https://images.unsplash.com/photo-1511285560929-80b456fea0bc?w=400&q=80',
        'https://images.unsplash.com/photo-1464366400600-7168b8af9bc3?w=400&q=80',
        'https://images.unsplash.com/photo-1519741497674-611481863552?w=400&q=80',
      ],
      modalWhatToExpect: 'Our lead photographer arrives 30 minutes early for a venue walkthrough and lighting assessment. Expect candid, editorial-quality shots throughout your event — from getting-ready moments to the last dance. You\'ll receive same-day social media previews and a full edited gallery within 2 weeks.',
      modalServiceType: 'Full Coverage',
      modalIncludes: ['Lead Photographer + Assistant', 'Same-Day Social Previews', 'Online Gallery (500+ Edited Photos)', 'Print Release & High-Res Files', 'Coverage of All Key Moments'],
    },
  },

  videography: {
    slug: 'videography',
    title: 'Videographers',
    singular: 'Videographer',
    headline: 'Tell Denver\'s\nEvent Stories.',
    subheadline: 'From cinematic wedding films to corporate highlight reels — bring your vision to Denver\'s biggest events.',
    heroImage: 'https://images.unsplash.com/photo-1579187707643-35646d22b596?w=800&q=80',
    heroImage2: 'https://images.unsplash.com/photo-1492691527719-9d1e07e534b4?w=800&q=80',
    metaTitle: 'Partner With Eventism as a Videographer | Event Video in Denver',
    metaDescription: 'Join Denver\'s top event platform as a videography partner. Film at premium venues and grow your business.',
    benefitCards: [
      { icon: 'VideoCamera', title: 'Cinematic Opportunities', desc: 'Film at Denver\'s most stunning venues — from rustic barns to downtown penthouses.' },
      { icon: 'ChartLineUp', title: 'Growing Demand', desc: 'Video is the fastest-growing event service. More clients want professional event films than ever.' },
      { icon: 'CurrencyDollar', title: 'Premium Packages', desc: 'Offer highlight reels, full-length films, drone footage, and same-day edits at your prices.' },
      { icon: 'Megaphone', title: 'Showreel Showcase', desc: 'Your vendor profile features your best work. Clients watch before they book.' },
      { icon: 'Star', title: 'Creative Control', desc: 'Your style, your vision. Clients choose you because they love your work.' },
    ],
    strategySteps: [
      { num: '01', title: 'Venues Partner With Us', desc: 'Denver\'s top venues trust Eventism to connect them with quality videographers.' },
      { num: '02', title: 'Video Demand Is Exploding', desc: '80% of couples now want a wedding film. Corporate clients want event recaps. You fill that need.' },
      { num: '03', title: 'We Match You to Events', desc: 'Our platform recommends you based on your style, gear, and availability.' },
      { num: '04', title: 'Every Film Builds Your Brand', desc: 'Great work leads to great reviews leads to more bookings. We amplify your best projects.' },
    ],
    stats: [
      { value: '$2,200', label: 'Average event videography booking' },
      { value: '80%', label: 'Of couples now want wedding films' },
      { value: '50+', label: 'Venue partners in our network' },
    ],
    galleryImages: [
      'https://images.unsplash.com/photo-1579187707643-35646d22b596?w=600&q=80',
      'https://images.unsplash.com/photo-1492691527719-9d1e07e534b4?w=600&q=80',
      'https://images.unsplash.com/photo-1516035069371-29a1b244cc32?w=600&q=80',
      'https://images.unsplash.com/photo-1540039155733-5bb30b53aa14?w=600&q=80',
    ],
    faqs: [
      { q: 'What types of video are in demand?', a: 'Wedding films, corporate recaps, social media clips, drone footage, and live-stream coverage.' },
      { q: 'Do I need drone certification?', a: 'For drone footage, yes. But standard videography doesn\'t require it.' },
      { q: 'Can I do photo and video?', a: 'Yes! List both services on your profile for maximum bookings.' },
      { q: 'What\'s the typical turnaround?', a: 'You set your own timelines. Most clients expect 4-8 weeks for edited films.' },
      { q: 'How does pricing work?', a: 'You set your rates — hourly, per event, or custom packages. Clients see pricing upfront.' },
    ],
    ctaText: 'Start Filming Denver\'s Events',
    ctaSubtext: 'Your lens tells stories that last forever.',
    sampleListing: {
      name: 'Mile High Films',
      tagline: 'Cinematic event films for the modern era',
      rating: 4.9, reviews: 36,
      priceLabel: 'From $1,800/event',
      tags: ['Cinematic', 'Weddings', 'Corporate', 'Drone'],
      features: ['4K Production', 'Same-Day Highlight', 'Drone Footage', 'Social Clips'],
    },
    sampleMarketingPage: {
      headline: 'Your Event Deserves a Cinematic Film',
      subline: 'Mile High Films creates stunning event videos that capture the energy, emotion, and details of your celebration.',
      testimonial: '"The highlight reel had our entire family in tears. It\'s like reliving the best day of our lives every time we watch it." — The Garcias',
      packages: [
        { name: 'Highlight', price: '$1,800', desc: '3-min highlight reel' },
        { name: 'Feature', price: '$3,000', desc: 'Full ceremony + recap' },
        { name: 'Cinematic', price: '$5,000', desc: 'Full-day + drone + edits' },
      ],
    },
    platformPreview: {
      cardTitle: 'Cinematic Event Highlight Film',
      cardDescription: 'Cinematic 4K event films with drone footage and same-day edits',
      cardCategory: 'Videography',
      cardSubcategory: 'Cinematic',
      cardPrice: '$1,800.00',
      cardPriceUnit: '/event',
      cardPriceLabel: 'Per Event',
      cardBadges: ['Featured', 'On-Site'],
      cardImage: 'https://images.unsplash.com/photo-1579187707643-35646d22b596?w=600&q=80',
      modalImages: [
        'https://images.unsplash.com/photo-1579187707643-35646d22b596?w=800&q=80',
        'https://images.unsplash.com/photo-1492691527719-9d1e07e534b4?w=400&q=80',
        'https://images.unsplash.com/photo-1516035069371-29a1b244cc32?w=400&q=80',
        'https://images.unsplash.com/photo-1540039155733-5bb30b53aa14?w=400&q=80',
        'https://images.unsplash.com/photo-1579187707643-35646d22b596?w=400&q=80',
      ],
      modalWhatToExpect: 'Our cinematographer arrives with professional cinema-grade equipment for a full day of coverage. Expect drone shots of the venue, candid guest moments, ceremony footage, and a beautifully edited highlight reel delivered within 4 weeks. Same-day social clips available for immediate sharing.',
      modalServiceType: 'Full Film Package',
      modalIncludes: ['4K Cinema Camera Coverage', 'Licensed Drone Footage', '3-Minute Highlight Reel', 'Full Ceremony Edit', 'Same-Day Social Media Clips'],
    },
  },

  bands: {
    slug: 'bands',
    title: 'Bands & Cover Bands',
    singular: 'Band',
    headline: 'Play Denver\'s\nBiggest Stages.',
    subheadline: 'Get your band booked at weddings, galas, and corporate events across Denver. We bring the audience — you bring the energy.',
    heroImage: 'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=800&q=80',
    heroImage2: 'https://images.unsplash.com/photo-1501386761578-eac5c94b800a?w=800&q=80',
    metaTitle: 'Partner With Eventism as a Band | Live Music Bookings in Denver',
    metaDescription: 'Join Denver\'s top event platform as a band or cover band. Get booked at premium venues and events.',
    benefitCards: [
      { icon: 'MusicNotes', title: 'Premium Venue Gigs', desc: 'Play at Denver\'s best event spaces — from intimate jazz clubs to grand ballrooms.' },
      { icon: 'ChartLineUp', title: 'Consistent Bookings', desc: 'Wedding season, holiday parties, corporate events — there\'s always a stage waiting for you.' },
      { icon: 'CurrencyDollar', title: 'Band-Level Pricing', desc: 'Set rates for your full band. Offer different configurations (duo, trio, full band).' },
      { icon: 'Megaphone', title: 'Demo Reels & Setlists', desc: 'Showcase your sound with audio samples, setlists, and videos on your profile.' },
      { icon: 'UsersThree', title: 'Genre Matching', desc: 'Our platform matches event vibes to your genre. Jazz for galas, rock for parties, country for weddings.' },
    ],
    strategySteps: [
      { num: '01', title: 'Venues Need Live Music', desc: 'Event venues love live bands. It\'s the #1 requested entertainment upgrade.' },
      { num: '02', title: 'We Match You to Events', desc: 'Client specifies their vibe, we recommend your band. No more cold-calling venue managers.' },
      { num: '03', title: 'Book the Full Band', desc: 'Clients book your preferred configuration. You manage your own members and setlist.' },
      { num: '04', title: 'Build a Following', desc: 'Great performances = great reviews = more bookings. Our platform amplifies your reputation.' },
    ],
    stats: [
      { value: '$3,000', label: 'Average band booking per event' },
      { value: '90%', label: 'Of galas request live music' },
      { value: '50+', label: 'Venue partners in the network' },
    ],
    galleryImages: [
      'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=600&q=80',
      'https://images.unsplash.com/photo-1501386761578-eac5c94b800a?w=600&q=80',
      'https://images.unsplash.com/photo-1470229722913-7c0e2dbbafd3?w=600&q=80',
      'https://images.unsplash.com/photo-1459749411175-04bf5292ceea?w=600&q=80',
    ],
    faqs: [
      { q: 'Can we list multiple band configurations?', a: 'Yes! Offer solo, duo, trio, and full band options with different pricing.' },
      { q: 'What genres are most in demand?', a: 'Cover bands, jazz, pop/rock, country, and Latin music are top requests in Denver.' },
      { q: 'Do we bring our own equipment?', a: 'Typically yes, but many venues have PA systems. You\'ll see venue specs before accepting.' },
      { q: 'How far in advance are bookings?', a: 'Wedding bookings often come 6-12 months ahead. Corporate events 2-4 weeks.' },
      { q: 'Can original bands join too?', a: 'Absolutely. Original artists are great for festivals, showcases, and unique events.' },
    ],
    ctaText: 'Get Your Band Booked',
    ctaSubtext: 'Denver\'s events need your sound.',
    sampleListing: {
      name: 'The Copper Tones',
      tagline: 'Cover band specialists — from Motown to modern hits',
      rating: 4.8, reviews: 54,
      priceLabel: 'From $2,000/event',
      tags: ['Cover Band', 'Weddings', 'Corporate', 'Jazz'],
      features: ['3-5 Piece Configurations', 'Custom Setlists', 'MC Services', 'Sound Equipment'],
    },
    sampleMarketingPage: {
      headline: 'Live Music That Gets Every Guest on the Dance Floor',
      subline: 'The Copper Tones play everything from Sinatra to Beyonce — the perfect soundtrack for your Denver event.',
      testimonial: '"They had every generation dancing! From grandma to the college kids — the energy was incredible." — Olivia R., Event Planner',
      packages: [
        { name: 'Duo', price: '$1,200', desc: 'Acoustic 2-hour set' },
        { name: 'Full Band', price: '$2,500', desc: '4-piece, 3 hours' },
        { name: 'Premium', price: '$4,000', desc: '5-piece, full night' },
      ],
    },
    platformPreview: {
      cardTitle: 'Live Cover Band Performance',
      cardDescription: 'Versatile cover band playing Motown to modern hits',
      cardCategory: 'Entertainment',
      cardSubcategory: 'Live Music',
      cardPrice: '$2,000.00',
      cardPriceUnit: '/event',
      cardPriceLabel: 'Per Event',
      cardBadges: ['Featured', 'On-Site'],
      cardImage: 'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=600&q=80',
      modalImages: [
        'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=800&q=80',
        'https://images.unsplash.com/photo-1501386761578-eac5c94b800a?w=400&q=80',
        'https://images.unsplash.com/photo-1470229722913-7c0e2dbbafd3?w=400&q=80',
        'https://images.unsplash.com/photo-1459749411175-04bf5292ceea?w=400&q=80',
        'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=400&q=80',
      ],
      modalWhatToExpect: 'The Copper Tones arrive 90 minutes before performance for full sound check and stage setup. Expect 3 hours of non-stop live music spanning decades — from classic Motown and jazz standards to modern pop hits. The band reads the room and adjusts energy levels to keep your dance floor packed all night.',
      modalServiceType: 'Full Band (4-Piece)',
      modalIncludes: ['4-Piece Live Band', 'Professional Sound System', 'Stage Lighting', 'Custom Setlist Consultation', 'MC Services Between Sets'],
    },
  },

  bartender: {
    slug: 'bartender',
    title: 'TIPS Certified Bartenders',
    singular: 'Bartender',
    headline: 'Pour at Denver\'s\nBest Events.',
    subheadline: 'TIPS certified and ready to work? Get booked at premium events across Denver — weddings, galas, corporate parties, and more.',
    heroImage: 'https://images.unsplash.com/photo-1514362545857-3bc16c4c7d1b?w=800&q=80',
    heroImage2: 'https://images.unsplash.com/photo-1551024709-8f23befc6f87?w=800&q=80',
    metaTitle: 'TIPS Certified Bartender Jobs in Denver | Partner With Eventism',
    metaDescription: 'Join Denver\'s top event platform as a TIPS certified bartender. Get booked at premium events with great pay.',
    benefitCards: [
      { icon: 'Martini', title: 'Premium Events Only', desc: 'Work at Denver\'s best venues — upscale weddings, corporate galas, and VIP parties.' },
      { icon: 'ChartLineUp', title: 'Flexible Schedule', desc: 'Pick up shifts that work for you. Weekends, weeknights, or holidays — your choice.' },
      { icon: 'CurrencyDollar', title: 'Top Rates + Tips', desc: 'Competitive hourly rates plus tips. TIPS certification means premium pay.' },
      { icon: 'Certificate', title: 'TIPS Certification Valued', desc: 'Your certification isn\'t just a requirement — it\'s a competitive advantage. Clients pay more for safety.' },
      { icon: 'Star', title: 'Build Your Reputation', desc: 'Client reviews and ratings help you stand out and command higher rates over time.' },
    ],
    strategySteps: [
      { num: '01', title: 'Events Need Bartenders', desc: 'Almost every event needs bar service. From open bars to craft cocktail stations.' },
      { num: '02', title: 'TIPS Certification = Trust', desc: 'Venues and clients require TIPS certified bartenders for liability. You\'re already qualified.' },
      { num: '03', title: 'We Match You to Events', desc: 'Tell us your availability and preferred event types. We send you the right gigs.' },
      { num: '04', title: 'Fast Pay, Great Tips', desc: 'Get paid quickly after each event. Tips go directly to you — always.' },
    ],
    stats: [
      { value: '$35+', label: 'Average hourly rate for TIPS certified bartenders' },
      { value: '$200', label: 'Average tips per event shift' },
      { value: '50+', label: 'Events per month needing bartenders' },
    ],
    galleryImages: [
      'https://images.unsplash.com/photo-1514362545857-3bc16c4c7d1b?w=600&q=80',
      'https://images.unsplash.com/photo-1551024709-8f23befc6f87?w=600&q=80',
      'https://images.unsplash.com/photo-1470337458703-46ad1756a187?w=600&q=80',
      'https://images.unsplash.com/photo-1574006852726-31d021e38fd9?w=600&q=80',
    ],
    faqs: [
      { q: 'Do I need TIPS certification?', a: 'Yes, TIPS certification is required for all bartenders on the platform. It\'s a must for liability at events.' },
      { q: 'Can I choose which events I work?', a: 'Absolutely. You set your availability and accept or decline every gig.' },
      { q: 'What about bar equipment and supplies?', a: 'Most events provide bar setup. You may need to bring basic bar tools depending on the gig.' },
      { q: 'How quickly do I get paid?', a: 'Fast payouts after event completion. Tips are yours to keep immediately.' },
      { q: 'Can I bring my own cocktail menu?', a: 'Many clients love custom cocktail menus. It\'s a great way to stand out and earn more.' },
    ],
    ctaText: 'Start Bartending at Premium Events',
    ctaSubtext: 'Your TIPS certification just got more valuable.',
    sampleListing: {
      name: 'Tyler Brooks',
      tagline: 'TIPS certified mixologist with 6+ years of event experience',
      rating: 4.9, reviews: 71,
      priceLabel: 'From $35/hour',
      tags: ['TIPS Certified', 'Craft Cocktails', 'Weddings', 'Corporate'],
      features: ['Signature Cocktail Menu', 'Full Bar Setup', 'Speed Service', 'Custom Drink Design'],
    },
    sampleMarketingPage: {
      headline: 'Expert Bartending for Your Next Denver Event',
      subline: 'Tyler brings craft cocktail skills and TIPS-certified professionalism to weddings, galas, and corporate events.',
      testimonial: '"Tyler designed custom cocktails for our wedding that matched our colors and theme. Guests were blown away." — Megan & David',
      packages: [
        { name: 'Standard', price: '$35/hr', desc: 'Bar service, you provide alcohol' },
        { name: 'Full Service', price: '$50/hr', desc: 'Setup, service, and breakdown' },
        { name: 'Craft Experience', price: '$75/hr', desc: 'Custom cocktails + presentation' },
      ],
    },
    platformPreview: {
      cardTitle: 'Professional Bartender Service',
      cardDescription: 'TIPS certified mixologist with craft cocktail expertise',
      cardCategory: 'Staffing',
      cardSubcategory: 'Bar Service',
      cardPrice: '$45.00',
      cardPriceUnit: '/hour',
      cardPriceLabel: 'Per Hour',
      cardBadges: ['Featured', 'On-Site'],
      cardImage: 'https://images.unsplash.com/photo-1514362545857-3bc16c4c7d1b?w=600&q=80',
      modalImages: [
        'https://images.unsplash.com/photo-1514362545857-3bc16c4c7d1b?w=800&q=80',
        'https://images.unsplash.com/photo-1551024709-8f23befc6f87?w=400&q=80',
        'https://images.unsplash.com/photo-1470337458703-46ad1756a187?w=400&q=80',
        'https://images.unsplash.com/photo-1574006852726-31d021e38fd9?w=400&q=80',
        'https://images.unsplash.com/photo-1514362545857-3bc16c4c7d1b?w=400&q=80',
      ],
      modalWhatToExpect: 'Your TIPS-certified bartender arrives 1 hour before service with all professional bar tools. Expect smooth, fast service with craft cocktail options tailored to your event theme. Tyler can design a custom cocktail menu with signature drinks named for your event or couple.',
      modalServiceType: 'Full Service',
      modalIncludes: ['TIPS Certified Professional', 'Craft Cocktail Menu Design', 'Full Bar Setup & Breakdown', 'Garnish Preparation', 'Professional Bar Tools'],
    },
  },

  'photo-booth': {
    slug: 'photo-booth',
    title: 'Photo Booth Operators',
    singular: 'Photo Booth Operator',
    headline: 'Bring the Fun.\nCapture the Memories.',
    subheadline: 'Photo booths are the #1 entertainment add-on at events. Join Eventism and get booked at every party in Denver.',
    heroImage: 'https://images.unsplash.com/photo-1527529482837-4698179dc6ce?w=800&q=80',
    heroImage2: 'https://images.unsplash.com/photo-1530103862676-de8c9debad1d?w=800&q=80',
    metaTitle: 'Photo Booth Business in Denver | Partner With Eventism',
    metaDescription: 'Grow your photo booth business with Eventism. Get booked at weddings, corporate events, and parties across Denver.',
    benefitCards: [
      { icon: 'Camera', title: 'Every Event Wants One', desc: 'Photo booths are the most requested party add-on. Weddings, corporate events, birthdays — they all want one.' },
      { icon: 'ChartLineUp', title: 'Massive Demand', desc: 'Denver\'s event scene is booming. Photo booth operators are booked solid during peak seasons.' },
      { icon: 'CurrencyDollar', title: 'Great Margins', desc: 'Low overhead, high demand. Photo booth businesses have some of the best margins in events.' },
      { icon: 'Megaphone', title: 'Showcase Your Setup', desc: 'Feature your booth styles, backdrops, props, and print options on your profile.' },
      { icon: 'Star', title: 'Upsell Opportunities', desc: 'Offer custom templates, GIF booths, video messages, and social media sharing add-ons.' },
    ],
    strategySteps: [
      { num: '01', title: 'Venues Love Photo Booths', desc: 'Event venues recommend photo booths to every client. It\'s an easy upsell for them.' },
      { num: '02', title: 'We Connect You to Events', desc: 'When clients plan events through Eventism, we recommend your photo booth service.' },
      { num: '03', title: 'You Setup, They Smile', desc: 'Show up, setup your booth, and let the guests have fun. It\'s the most fun job at any event.' },
      { num: '04', title: 'Repeat Business Built In', desc: 'Happy clients book you again. Our review system ensures your great work gets seen.' },
    ],
    stats: [
      { value: '$1,200', label: 'Average photo booth booking' },
      { value: '#1', label: 'Most requested party add-on in Denver' },
      { value: '50+', label: 'Venue partners sending you business' },
    ],
    galleryImages: [
      'https://images.unsplash.com/photo-1527529482837-4698179dc6ce?w=600&q=80',
      'https://images.unsplash.com/photo-1530103862676-de8c9debad1d?w=600&q=80',
      'https://images.unsplash.com/photo-1541020230467-e33e4f96f7fc?w=600&q=80',
      'https://images.unsplash.com/photo-1511285560929-80b456fea0bc?w=600&q=80',
    ],
    faqs: [
      { q: 'What types of photo booths can I list?', a: 'Classic enclosed booths, open-air, mirror booths, 360 booths, GIF booths — all are welcome.' },
      { q: 'Do I need to provide props?', a: 'Props are a great add-on but not required. Many operators offer themed prop packages.' },
      { q: 'How long are typical bookings?', a: 'Most events book 2-4 hours of booth time. You set your minimum booking length.' },
      { q: 'Can I serve multiple events per day?', a: 'If your schedule allows it, absolutely. Many operators run 2-3 events on busy weekends.' },
      { q: 'What about print supplies?', a: 'You manage your own supplies and factor costs into your pricing.' },
    ],
    ctaText: 'Start Booking Photo Booth Gigs',
    ctaSubtext: 'Denver\'s parties need your booth.',
    sampleListing: {
      name: 'SnapBox Denver',
      tagline: 'Modern photo booth experiences for every event',
      rating: 4.9, reviews: 93,
      priceLabel: 'From $600/event',
      tags: ['360 Booth', 'Open Air', 'GIF Booth', 'Custom Prints'],
      features: ['Instant Prints', 'Digital Gallery', 'Custom Templates', 'Props Included'],
    },
    sampleMarketingPage: {
      headline: 'The Photo Booth Denver Events Can\'t Stop Talking About',
      subline: 'SnapBox Denver delivers modern, fun, and unforgettable photo booth experiences for weddings, corporate events, and parties.',
      testimonial: '"The 360 booth was the highlight of our entire event! Everyone was lining up all night." — Corporate Event at Blanc Denver',
      packages: [
        { name: 'Classic', price: '$600', desc: '2 hours, prints + digital' },
        { name: 'Party', price: '$900', desc: '3 hours, props + backdrop' },
        { name: '360 Experience', price: '$1,400', desc: '360 booth, full night' },
      ],
    },
    platformPreview: {
      cardTitle: '360 Photo Booth Experience',
      cardDescription: 'Interactive 360-degree video booth with instant sharing',
      cardCategory: 'Entertainment',
      cardSubcategory: 'Interactive',
      cardPrice: '$600.00',
      cardPriceUnit: '/event',
      cardPriceLabel: 'Per Event',
      cardBadges: ['Featured', 'Drop-off'],
      cardImage: 'https://images.unsplash.com/photo-1527529482837-4698179dc6ce?w=600&q=80',
      modalImages: [
        'https://images.unsplash.com/photo-1527529482837-4698179dc6ce?w=800&q=80',
        'https://images.unsplash.com/photo-1530103862676-de8c9debad1d?w=400&q=80',
        'https://images.unsplash.com/photo-1541020230467-e33e4f96f7fc?w=400&q=80',
        'https://images.unsplash.com/photo-1511285560929-80b456fea0bc?w=400&q=80',
        'https://images.unsplash.com/photo-1527529482837-4698179dc6ce?w=400&q=80',
      ],
      modalWhatToExpect: 'Our team arrives 1 hour before your event for full booth setup including backdrop, lighting, and props station. Guests step onto the platform for an automated 360-degree video capture with slow-motion effects. Instant sharing to social media and a digital gallery link sent to all participants.',
      modalServiceType: 'Full Setup',
      modalIncludes: ['360 Video Booth Platform', 'Custom Branded Overlay', 'Props & Backdrop', 'Instant Social Sharing', 'Digital Gallery for All Guests'],
    },
  },

  'live-artist': {
    slug: 'live-artist',
    title: 'Live Artists',
    singular: 'Live Artist',
    headline: 'Create Art.\nAt Every Event.',
    subheadline: 'Live painting, caricatures, digital art — bring your talent to Denver\'s most exciting events through Eventism.',
    heroImage: 'https://images.unsplash.com/photo-1460661419201-fd4cecdf8a8b?w=800&q=80',
    heroImage2: 'https://images.unsplash.com/photo-1513364776144-60967b0f800f?w=800&q=80',
    metaTitle: 'Live Event Artist in Denver | Partner With Eventism',
    metaDescription: 'Join Denver\'s event platform as a live artist. Paint at weddings, draw at parties, and grow your art business.',
    benefitCards: [
      { icon: 'PaintBrush', title: 'Unique Entertainment', desc: 'Live art is one of the most unique entertainment options at events. Clients love it.' },
      { icon: 'ChartLineUp', title: 'Premium Niche', desc: 'Live artists command premium rates because the experience is irreplaceable.' },
      { icon: 'CurrencyDollar', title: 'Art + Income', desc: 'Get paid to create art at beautiful venues. Many clients also purchase the finished piece.' },
      { icon: 'Megaphone', title: 'Portfolio on Display', desc: 'Your vendor profile showcases your style, medium, and past live-painting examples.' },
      { icon: 'Star', title: 'Unforgettable Experience', desc: 'A live artist at an event creates a memory that lasts forever — and clients remember you.' },
    ],
    strategySteps: [
      { num: '01', title: 'Events Want Something Special', desc: 'Planners are always looking for unique entertainment that wows guests.' },
      { num: '02', title: 'We Showcase Your Art', desc: 'Your portfolio on Eventism reaches event planners actively looking for live entertainment.' },
      { num: '03', title: 'Match Art to Event', desc: 'Watercolor for weddings, caricatures for corporate, digital for modern events — we match your style.' },
      { num: '04', title: 'Create, Earn, Repeat', desc: 'Every event is a chance to create art, earn income, and gain new fans.' },
    ],
    stats: [
      { value: '$1,500', label: 'Average live artist booking' },
      { value: '300%', label: 'Growth in live art requests since 2023' },
      { value: '50+', label: 'Venue partners in the network' },
    ],
    galleryImages: [
      'https://images.unsplash.com/photo-1460661419201-fd4cecdf8a8b?w=600&q=80',
      'https://images.unsplash.com/photo-1513364776144-60967b0f800f?w=600&q=80',
      'https://images.unsplash.com/photo-1541367777708-7905fe3296c0?w=600&q=80',
      'https://images.unsplash.com/photo-1460661419201-fd4cecdf8a8b?w=600&q=80',
    ],
    faqs: [
      { q: 'What types of live art are in demand?', a: 'Live painting, caricatures, digital art, calligraphy, and portrait sketching.' },
      { q: 'Do I keep the finished artwork?', a: 'Typically the client keeps it, but you can negotiate. Many artists create a piece for the client and sell prints.' },
      { q: 'How long are typical bookings?', a: 'Usually 2-4 hours. Some clients want the full event, others want a featured segment.' },
      { q: 'Do I need to bring my own supplies?', a: 'Yes, you provide your own materials and setup. Factor this into your pricing.' },
      { q: 'Can I sell prints or originals?', a: 'Absolutely! Many live artists use events as a showcase for their broader art business.' },
    ],
    ctaText: 'Start Creating at Denver Events',
    ctaSubtext: 'Your art deserves a live audience.',
    sampleListing: {
      name: 'Ana Mora Live Art',
      tagline: 'Live painting & illustration at your event',
      rating: 5.0, reviews: 19,
      priceLabel: 'From $1,000/event',
      tags: ['Live Painting', 'Weddings', 'Corporate', 'Portraits'],
      features: ['Live Event Painting', 'Guest Portraits', 'Custom Commissions', 'Digital Art'],
    },
    sampleMarketingPage: {
      headline: 'A Live Painting Created at Your Event — A Masterpiece to Keep',
      subline: 'Ana Mora paints your event in real-time, creating a one-of-a-kind artwork that captures the magic of the moment.',
      testimonial: '"Watching Ana paint our first dance in real-time was the most incredible surprise. The painting hangs in our living room." — The Nguyens',
      packages: [
        { name: 'Sketch', price: '$800', desc: '2 hours, charcoal sketch' },
        { name: 'Canvas', price: '$1,500', desc: '4 hours, oil painting' },
        { name: 'Grand', price: '$2,500', desc: 'Large canvas, full event' },
      ],
    },
    platformPreview: {
      cardTitle: 'Live Event Painting',
      cardDescription: 'Real-time oil painting of your event\'s most memorable moment',
      cardCategory: 'Entertainment',
      cardSubcategory: 'Visual Art',
      cardPrice: '$1,000.00',
      cardPriceUnit: '/event',
      cardPriceLabel: 'Per Event',
      cardBadges: ['Featured', 'On-Site'],
      cardImage: 'https://images.unsplash.com/photo-1460661419201-fd4cecdf8a8b?w=600&q=80',
      modalImages: [
        'https://images.unsplash.com/photo-1460661419201-fd4cecdf8a8b?w=800&q=80',
        'https://images.unsplash.com/photo-1513364776144-60967b0f800f?w=400&q=80',
        'https://images.unsplash.com/photo-1541367777708-7905fe3296c0?w=400&q=80',
        'https://images.unsplash.com/photo-1460661419201-fd4cecdf8a8b?w=400&q=80',
        'https://images.unsplash.com/photo-1513364776144-60967b0f800f?w=400&q=80',
      ],
      modalWhatToExpect: 'Ana arrives with her full painting setup — easel, canvas, oils, and lighting. She\'ll capture the most memorable moment of your event in real-time, working throughout the evening as guests watch the painting come to life. The completed piece is presented to you at the end of the event.',
      modalServiceType: 'Full Canvas',
      modalIncludes: ['Professional Canvas & Oil Paints', 'Easel & Studio Lighting', '4-Hour Live Painting Session', 'Finished Painting to Keep', 'Custom Framing Options'],
    },
  },

  entertainment: {
    slug: 'entertainment',
    title: 'General Entertainment',
    singular: 'Entertainer',
    headline: 'Entertain Denver.\nGet Booked.',
    subheadline: 'Magicians, comedians, MCs, fire performers, balloon artists — whatever your talent, Denver\'s events need you.',
    heroImage: 'https://images.unsplash.com/photo-1639682687092-0264e96cbb71?w=800&q=80',
    heroImage2: 'https://images.unsplash.com/photo-1768508948462-58962b3ab650?w=800&q=80',
    metaTitle: 'Event Entertainment in Denver | Partner With Eventism',
    metaDescription: 'Join Denver\'s top event platform as an entertainment vendor. Get booked at parties, corporate events, and celebrations.',
    benefitCards: [
      { icon: 'MagicWand', title: 'Every Talent Welcome', desc: 'Magicians, comedians, MCs, fire performers, face painters, balloon artists — if you entertain, we want you.' },
      { icon: 'ChartLineUp', title: 'Events Need Entertainment', desc: 'Every corporate event, birthday party, and holiday gala needs something special. That\'s you.' },
      { icon: 'CurrencyDollar', title: 'Set Your Own Rates', desc: 'From 30-minute sets to full-night entertainment packages — price it your way.' },
      { icon: 'Megaphone', title: 'Video & Media Showcase', desc: 'Upload performance videos, photos, and testimonials to your profile.' },
      { icon: 'Star', title: 'Stand Out', desc: 'Our platform helps unique entertainment get discovered by the right clients.' },
    ],
    strategySteps: [
      { num: '01', title: 'Events Are Experiences', desc: 'Modern events are about creating unforgettable moments. Entertainment is the key ingredient.' },
      { num: '02', title: 'We Find Your Perfect Audience', desc: 'We match your act to events where you\'ll shine. Kids\' party magician or corporate MC — we know the difference.' },
      { num: '03', title: 'Book Direct, No Agents', desc: 'Clients book you directly through the platform. No agent fees, no middlemen.' },
      { num: '04', title: 'Grow Your Act', desc: 'Build reviews, expand your offerings, and become Denver\'s go-to entertainment.' },
    ],
    stats: [
      { value: '$1,000', label: 'Average entertainment booking' },
      { value: '100+', label: 'Events per month in Denver' },
      { value: '50+', label: 'Venue partners in our network' },
    ],
    galleryImages: [
      'https://images.unsplash.com/photo-1639682687092-0264e96cbb71?w=600&q=80',
      'https://images.unsplash.com/photo-1768508948462-58962b3ab650?w=600&q=80',
      'https://images.unsplash.com/photo-1540039155733-5bb30b53aa14?w=600&q=80',
      'https://images.unsplash.com/photo-1501386761578-eac5c94b800a?w=600&q=80',
    ],
    faqs: [
      { q: 'What types of entertainment can I list?', a: 'Magic, comedy, MC/hosting, fire performance, balloon art, face painting, stilt walking, trivia hosting — anything!' },
      { q: 'Can I offer multiple services?', a: 'Yes! If you\'re a DJ who also MCs, or a magician who does balloon art — list it all.' },
      { q: 'What\'s the typical event length?', a: 'Ranges from 30-minute sets to 4+ hour performances depending on the event type.' },
      { q: 'Do I need liability insurance?', a: 'Recommended but not always required. Some venues require it — you\'ll know before accepting.' },
      { q: 'Can I bring assistants?', a: 'Absolutely. Include any technical requirements or support needs in your listing.' },
    ],
    ctaText: 'Start Entertaining Denver',
    ctaSubtext: 'Your talent deserves a stage.',
    sampleListing: {
      name: 'Denver Magic Co.',
      tagline: 'Close-up magic and stage shows for corporate & private events',
      rating: 4.9, reviews: 44,
      priceLabel: 'From $600/event',
      tags: ['Magic', 'Corporate', 'Family', 'Stage Shows'],
      features: ['Close-Up Magic', 'Stage Performances', 'Mentalism', 'Custom Acts'],
    },
    sampleMarketingPage: {
      headline: 'Mind-Blowing Entertainment for Your Denver Event',
      subline: 'Denver Magic Co. delivers jaw-dropping performances that your guests will be talking about long after the event.',
      testimonial: '"Our entire sales team was amazed. The close-up magic during cocktail hour was the perfect icebreaker." — Acme Corp Annual Retreat',
      packages: [
        { name: 'Cocktail', price: '$600', desc: '1-hour walk-around magic' },
        { name: 'Show', price: '$1,200', desc: '45-min stage performance' },
        { name: 'Full Night', price: '$2,000', desc: 'Walk-around + stage + MC' },
      ],
    },
    platformPreview: {
      cardTitle: 'Close-Up Magic & Stage Show',
      cardDescription: 'Mind-blowing close-up magic and mentalism for corporate & private events',
      cardCategory: 'Entertainment',
      cardSubcategory: 'Magic',
      cardPrice: '$600.00',
      cardPriceUnit: '/event',
      cardPriceLabel: 'Per Event',
      cardBadges: ['Featured', 'On-Site'],
      cardImage: 'https://images.unsplash.com/photo-1639682687092-0264e96cbb71?w=600&q=80',
      modalImages: [
        'https://images.unsplash.com/photo-1639682687092-0264e96cbb71?w=800&q=80',
        'https://images.unsplash.com/photo-1540039155733-5bb30b53aa14?w=400&q=80',
        'https://images.unsplash.com/photo-1501386761578-eac5c94b800a?w=400&q=80',
        'https://images.unsplash.com/photo-1639682687092-0264e96cbb71?w=400&q=80',
        'https://images.unsplash.com/photo-1540039155733-5bb30b53aa14?w=400&q=80',
      ],
      modalWhatToExpect: 'Our magician arrives 30 minutes early for stage or space assessment. During cocktail hour, expect intimate close-up magic that leaves small groups speechless. For the main event, a 45-minute stage show with audience participation, mentalism, and comedy that keeps every guest engaged.',
      modalServiceType: 'Full Night Package',
      modalIncludes: ['1-Hour Walk-Around Close-Up Magic', '45-Minute Stage Performance', 'Audience Participation Segments', 'Mentalism & Comedy Elements', 'MC Services Available'],
    },
  },

  venues: {
    slug: 'venues',
    title: 'Venues & Event Centers',
    singular: 'Venue',
    headline: 'Fill Your\nCalendar.',
    subheadline: 'List your venue on Eventism and get discovered by event planners, corporations, and wedding couples across Denver.',
    heroImage: 'https://images.unsplash.com/photo-1519167758481-83f550bb49b3?w=800&q=80',
    heroImage2: 'https://images.unsplash.com/photo-1464366400600-7168b8af9bc3?w=800&q=80',
    metaTitle: 'List Your Venue on Eventism | Event Venue Marketing in Denver',
    metaDescription: 'Partner with Denver\'s top event platform. Get your venue in front of event planners, corporate clients, and wedding couples.',
    benefitCards: [
      { icon: 'Buildings', title: 'Reach Event Planners', desc: 'Get your venue in front of corporate planners, wedding coordinators, and party hosts actively searching.' },
      { icon: 'ChartLineUp', title: 'Fill Empty Dates', desc: 'Our marketing targets clients for your off-peak days and slow seasons.' },
      { icon: 'CurrencyDollar', title: 'Increase Revenue', desc: 'More bookings + our vendor network handling services = higher venue revenue per event.' },
      { icon: 'Megaphone', title: 'Professional Listing', desc: 'Beautiful venue page with photos, virtual tours, pricing, capacity, and amenities.' },
      { icon: 'UsersThree', title: 'Vendor Network Access', desc: 'Our vendor network provides catering, entertainment, and staffing — making your venue a full-service option.' },
    ],
    strategySteps: [
      { num: '01', title: 'List Your Venue', desc: 'Create a stunning venue profile with photos, pricing, availability, and amenities.' },
      { num: '02', title: 'We Market It For You', desc: 'Custom marketing pages, SEO optimization, and targeted outreach to event planners.' },
      { num: '03', title: 'Clients Book Directly', desc: 'Event planners discover your venue and book through the platform. We handle inquiries and scheduling.' },
      { num: '04', title: 'Full-Service Events', desc: 'Our vendor network provides everything else — catering, DJ, photos — making your venue the obvious choice.' },
    ],
    stats: [
      { value: '60%', label: 'Average increase in bookings for partner venues' },
      { value: '$15K', label: 'Average monthly revenue increase for active venues' },
      { value: '500+', label: 'Event planners using our platform' },
    ],
    galleryImages: [
      'https://images.unsplash.com/photo-1519167758481-83f550bb49b3?w=600&q=80',
      'https://images.unsplash.com/photo-1464366400600-7168b8af9bc3?w=600&q=80',
      'https://images.unsplash.com/photo-1519741497674-611481863552?w=600&q=80',
      'https://images.unsplash.com/photo-1530103862676-de8c9debad1d?w=600&q=80',
    ],
    faqs: [
      { q: 'What types of venues can list?', a: 'Hotels, ballrooms, rooftops, restaurants, barns, corporate centers, gardens — any event space.' },
      { q: 'Do you handle catering and vendors?', a: 'Yes! Our vendor network provides all services. You can also bring your own preferred vendors.' },
      { q: 'What\'s the commission structure?', a: 'A small referral fee per booking. You keep the vast majority of your venue rental fee.' },
      { q: 'Can I manage my own availability?', a: 'Full calendar control. Block dates, set pricing by day/season, and manage inquiries.' },
      { q: 'Will this compete with my direct bookings?', a: 'No — Eventism is an additional channel. We bring you clients you wouldn\'t have found otherwise.' },
    ],
    ctaText: 'List Your Venue Today',
    ctaSubtext: 'Denver\'s event planners are searching right now.',
    sampleListing: {
      name: 'The Summit at Mile High',
      tagline: 'Denver\'s premier rooftop event venue with mountain views',
      rating: 4.8, reviews: 127,
      priceLabel: 'From $3,500/event',
      tags: ['Rooftop', 'Mountain Views', 'Corporate', 'Weddings'],
      features: ['Panoramic Views', 'Full Kitchen', 'In-House AV', '300 Capacity'],
    },
    sampleMarketingPage: {
      headline: 'Host Your Event Above the Denver Skyline',
      subline: 'The Summit at Mile High offers breathtaking mountain views, world-class amenities, and full-service event support.',
      testimonial: '"The views alone made our event unforgettable. Add in the incredible service and it was the best venue we\'ve ever used." — Google Denver, Holiday Party',
      packages: [
        { name: 'Half Day', price: '$3,500', desc: '5 hours, basic AV' },
        { name: 'Full Day', price: '$6,000', desc: '10 hours, premium setup' },
        { name: 'Exclusive', price: '$10,000', desc: 'Full buyout, all amenities' },
      ],
    },
    platformPreview: {
      cardTitle: 'Premium Rooftop Venue',
      cardDescription: 'Stunning rooftop event space with panoramic mountain views',
      cardCategory: 'Venue',
      cardSubcategory: 'Rooftop',
      cardPrice: '$3,500.00',
      cardPriceUnit: '/event',
      cardPriceLabel: 'Per Event',
      cardBadges: ['Featured', 'Venue'],
      cardImage: 'https://images.unsplash.com/photo-1519167758481-83f550bb49b3?w=600&q=80',
      modalImages: [
        'https://images.unsplash.com/photo-1519167758481-83f550bb49b3?w=800&q=80',
        'https://images.unsplash.com/photo-1464366400600-7168b8af9bc3?w=400&q=80',
        'https://images.unsplash.com/photo-1519741497674-611481863552?w=400&q=80',
        'https://images.unsplash.com/photo-1530103862676-de8c9debad1d?w=400&q=80',
        'https://images.unsplash.com/photo-1519167758481-83f550bb49b3?w=400&q=80',
      ],
      modalWhatToExpect: 'The Summit offers an unparalleled rooftop experience with 360-degree views of the Rocky Mountains and Denver skyline. The space accommodates up to 300 guests with flexible indoor/outdoor configurations. In-house AV, full commercial kitchen, and dedicated event coordinator included with every booking.',
      modalServiceType: 'Full Day Rental',
      modalIncludes: ['300-Person Capacity', 'Panoramic Mountain Views', 'In-House AV System', 'Full Commercial Kitchen', 'Dedicated Event Coordinator'],
    },
  },

  staffing: {
    slug: 'staffing',
    title: 'Event Staff',
    singular: 'Staff Member',
    headline: 'Work Denver\'s\nBest Events.',
    subheadline: 'Servers, bartenders, event coordinators, setup crews — get premium event shifts with great pay through Eventism.',
    heroImage: 'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=800&q=80',
    heroImage2: 'https://images.unsplash.com/photo-1530103862676-de8c9debad1d?w=600&q=80',
    metaTitle: 'Event Staffing Jobs in Denver | Partner With Eventism',
    metaDescription: 'Join Denver\'s top event platform for event staffing. Servers, bartenders, and coordinators needed for premium events.',
    benefitCards: [
      { icon: 'UsersThree', title: 'Premium Events', desc: 'Work at Denver\'s best venues — hotels, country clubs, corporate campuses, and private estates.' },
      { icon: 'ChartLineUp', title: 'Flexible Scheduling', desc: 'Pick up shifts that fit your life. Full-time, part-time, or just weekends.' },
      { icon: 'CurrencyDollar', title: 'Great Pay + Tips', desc: 'Competitive hourly rates plus tips. Event staffing often pays more than traditional hospitality.' },
      { icon: 'Certificate', title: 'Grow Your Skills', desc: 'Work different event types and venues. Build experience in high-end hospitality.' },
      { icon: 'Star', title: 'Build Your Reputation', desc: 'Great reviews lead to premium gigs and repeat bookings at the best venues.' },
    ],
    strategySteps: [
      { num: '01', title: 'Events Need Staff', desc: 'Every event needs servers, bartenders, coordinators, and setup/teardown crews.' },
      { num: '02', title: 'We Match You to Gigs', desc: 'Tell us your availability, skills, and preferences. We send you the right shifts.' },
      { num: '03', title: 'Show Up and Shine', desc: 'Arrive, work the event, deliver great service. It\'s that simple.' },
      { num: '04', title: 'Fast Pay, Great Tips', desc: 'Get paid quickly. Tips go directly to you. Build a reputation for higher rates.' },
    ],
    stats: [
      { value: '$25-45', label: 'Hourly rate range for event staff' },
      { value: '$150', label: 'Average tips per event shift' },
      { value: '100+', label: 'Events per month needing staff' },
    ],
    galleryImages: [
      'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=600&q=80',
      'https://images.unsplash.com/photo-1530103862676-de8c9debad1d?w=600&q=80',
      'https://images.unsplash.com/photo-1541020230467-e33e4f96f7fc?w=600&q=80',
      'https://images.unsplash.com/photo-1519167758481-83f550bb49b3?w=600&q=80',
    ],
    faqs: [
      { q: 'What positions are available?', a: 'Servers, bartenders, event coordinators, coat check, valet, setup/teardown crews, and more.' },
      { q: 'Do I need experience?', a: 'Some experience is helpful but not always required. We have gigs for all skill levels.' },
      { q: 'Is this W-2 or 1099?', a: 'Independent contractor (1099). You have full flexibility over your schedule.' },
      { q: 'What should I wear?', a: 'Dress code varies by event. You\'ll get specific instructions with each booking.' },
      { q: 'How quickly do I get paid?', a: 'Fast payouts after event completion. Tips are yours immediately.' },
    ],
    ctaText: 'Start Working Premium Events',
    ctaSubtext: 'Denver\'s best events need you.',
    sampleListing: {
      name: 'Jordan Ellis',
      tagline: 'Experienced event server & coordinator with 4+ years',
      rating: 4.9, reviews: 38,
      priceLabel: 'From $28/hour',
      tags: ['Server', 'Coordinator', 'Bartender', 'Setup/Teardown'],
      features: ['Fine Dining Experience', 'TIPS Certified', 'Bilingual', 'Team Leadership'],
    },
    sampleMarketingPage: {
      headline: 'Professional Event Staff You Can Count On',
      subline: 'Jordan brings fine-dining professionalism and warm hospitality to every event — from intimate dinners to 500-person galas.',
      testimonial: '"Jordan ran our service team flawlessly. Guests kept complimenting how attentive and professional the staff was." — Wedding at Lionsgate',
      packages: [
        { name: 'Server', price: '$28/hr', desc: 'Food & beverage service' },
        { name: 'Lead Server', price: '$35/hr', desc: 'Service + team coordination' },
        { name: 'Coordinator', price: '$45/hr', desc: 'Full event management' },
      ],
    },
    platformPreview: {
      cardTitle: 'Professional Event Server',
      cardDescription: 'Experienced server with fine-dining and event coordination skills',
      cardCategory: 'Staffing',
      cardSubcategory: 'Service',
      cardPrice: '$28.00',
      cardPriceUnit: '/hour',
      cardPriceLabel: 'Per Hour',
      cardBadges: ['Featured', 'On-Site'],
      cardImage: 'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=600&q=80',
      modalImages: [
        'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=800&q=80',
        'https://images.unsplash.com/photo-1530103862676-de8c9debad1d?w=400&q=80',
        'https://images.unsplash.com/photo-1541020230467-e33e4f96f7fc?w=400&q=80',
        'https://images.unsplash.com/photo-1519167758481-83f550bb49b3?w=400&q=80',
        'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=400&q=80',
      ],
      modalWhatToExpect: 'Your event server arrives 30 minutes before service in professional attire, ready to deliver seamless hospitality. Expect polished table service, attentive drink refills, and discreet plate clearing. Jordan coordinates with your caterer and event planner to ensure every guest feels attended to.',
      modalServiceType: 'Full Shift',
      modalIncludes: ['Professional Attire Provided', 'Fine-Dining Service Standards', 'Bar & Table Service', 'Setup & Teardown Assistance', 'Team Coordination Available'],
    },
  },
};

export const ALL_VENDOR_CATEGORIES = Object.values(VENDOR_CONFIGS).map(v => ({
  slug: v.slug,
  title: v.title,
  singular: v.singular,
}));

export default VENDOR_CONFIGS;
